/**
 * A session's status in words and tone, refined by its adapter's canonical state: an ended
 * session that failed or was stopped says so, a live one that has not finished its first
 * turn says it is starting. Everything else reads as the four-state status. A live state
 * the adapter only guessed (Codex liveness 'inferred') reads as a guess: "Probably working".
 */
import { SESSION_STATUS_LABEL, agentKindLabel, agentKindOf, ownWorkOf, sessionStateOf } from '@orgus/protocol';
import type { Session } from '@orgus/protocol';
import { sessionRowStatus, sessionTone } from '@orgus/ui';
import type { PillStatus, RowStatusProps, StatusTone } from '@orgus/ui';
import { OUTCOME_LABEL, sessionOutcome } from '../sessions/outcome.ts';

/** A live session whose turn ended with its own background work out (ORGU-132). */
export const OWN_WORK_LABEL = 'Waiting on own work';

type StateInput =Pick<Session, 'status' | 'turns' | 'toolCalls' | 'lastText' | 'state'> & Partial<Pick<Session, 'ext'>>;

/** Is this session's live state a guess (its adapter could not see its process)? */
export function isLivenessGuessed(s: Pick<Session, 'status'> & Partial<Pick<Session, 'ext'>>): boolean {
  return s.status !== 'ended' && s.ext?.liveness === 'inferred';
}

/** Why a guessed state is a guess, for a tooltip; null when the state is known. */
export function livenessGuessNote(s: Pick<Session, 'status'> & Partial<Pick<Session, 'ext'>>, machineName?: string): string | null {
  if (!isLivenessGuessed(s)) return null;
  const n = typeof s.ext?.processCount === 'number' ? s.ext.processCount : null;
  const where = machineName ? ` on ${machineName}` : '';
  const count = n === null ? 'Codex processes run' : `${n} Codex ${n === 1 ? 'process runs' : 'processes run'}`;
  return `Guessed: ${count}${where} and this session was written to recently. Codex keeps no process records, so Orgus cannot tell which process is which.`;
}

function baseLabel(s: StateInput): string {
  const outcome = sessionOutcome(s);
  if (outcome) return OUTCOME_LABEL[outcome];
  switch (sessionStateOf(s)) {
    case 'failed':
      return 'Failed';
    case 'stopped':
      return 'Stopped';
    case 'starting':
      return 'Starting';
    default:
      return ownWorkOf(s) ? OWN_WORK_LABEL : SESSION_STATUS_LABEL[s.status];
  }
}

export function sessionStateLabel(s: StateInput): string {
  const label = baseLabel(s);
  return isLivenessGuessed(s) ? `Probably ${label.charAt(0).toLowerCase()}${label.slice(1)}` : label;
}

export function sessionStateTone(s: StateInput): StatusTone {
  return sessionOutcome(s) === 'failed' ? 'failed' : sessionTone(s);
}

/** Who speaks in a session's timeline: "Claude" for Claude Code, else the agent's name. */
export function speakerOf(s: Pick<Session, 'agentKind'>): string {
  const kind = agentKindOf(s);
  return kind === 'claude-code' ? 'Claude' : agentKindLabel(kind);
}

/**
 * The row status (tone, icon, label) for a session list: the four-state status, refined by
 * the canonical state (failed, stopped, starting).
 */
export function sessionRowStatusOf(s: StateInput): Pick<RowStatusProps, 'tone' | 'icon' | 'label'> {
  const base = sessionRowStatus(s.status);
  const state = sessionStateOf(s);
  const outcome = sessionOutcome(s);
  // An ended session says how it came out: Succeeded (grey, the done glyph), Failed or Stopped.
  if (outcome === 'succeeded') return { tone: 'neutral', icon: 'done', label: OUTCOME_LABEL.succeeded };
  const refined: Pick<RowStatusProps, 'tone' | 'icon' | 'label'> =
    outcome === 'failed'
      ? { tone: 'danger', icon: 'failed', label: 'Failed' }
      : state === 'stopped'
        ? { tone: 'neutral', icon: 'stopped', label: 'Stopped' }
        : state === 'starting'
          ? { ...base, label: 'Starting' }
          : ownWorkOf(s)
            ? { ...sessionRowStatus('idle'), label: OWN_WORK_LABEL }
            : base;
  // A guess keeps the plain label in a row (the column is narrow); the row's tooltip and the
  // drawer's pill say it is a guess.
  return refined;
}

/**
 * The drawer's status pill, refined the same way: failed and stopped say so, a first turn
 * reads as starting, and a guessed live state reads as "Probably ...".
 */
export function sessionStateOfPill(s: StateInput, base: PillStatus, baseLabel?: string): { status: PillStatus; label?: string } {
  const state = sessionStateOf(s);
  const outcome = sessionOutcome(s);
  // ORGU-132: an exited process says Ended and how it ended; a turn that ended with its own work
  // out says so, never Working.
  const out: { status: PillStatus; label?: string } =
    outcome === 'succeeded'
      ? { status: 'done', label: `Ended, ${OUTCOME_LABEL.succeeded.toLowerCase()}` }
      : outcome === 'failed'
      ? { status: 'failed', label: `Ended, ${OUTCOME_LABEL.failed.toLowerCase()}` }
      : state === 'stopped'
        ? { status: 'ended', label: s.status === 'ended' ? 'Ended, stopped' : 'Stopped' }
        : state === 'starting'
          ? { status: base, label: 'Starting' }
          : ownWorkOf(s)
            ? { status: 'idle', label: OWN_WORK_LABEL }
            : { status: base, ...(baseLabel ? { label: baseLabel } : {}) };
  if (!isLivenessGuessed(s)) return out;
  const label = out.label ?? SESSION_STATUS_LABEL[s.status];
  return { ...out, label: `Probably ${label.charAt(0).toLowerCase()}${label.slice(1)}` };
}
